import { Grid, Spinner } from '@chakra-ui/react'
import styled from '@emotion/styled'
import { Component } from 'react'
import {
  EncodingCounters,
  EncodingCounterValues,
  EncodingProductionOrderRows,
  ProductInfoField,
  ProductionOrder,
  ProductionOrderRow,
  ProductionOrders,
  TmrProduct,
  WorkOrder,
  WorkOrderRow,
  WorkOrderRows,
  WorkOrders
} from 'stylewhere/api'
import {
  Box,
  Button,
  EncodingCountersBox,
  EncodingProduct,
  FormSchemaForm,
  InfoBox,
  KeyValueRow,
  Spacer,
  Text
} from 'stylewhere/components'
import { EncodingExtensions } from 'stylewhere/extensions'
import { AppStore, FormSchema, FormSchemaData } from 'stylewhere/shared'
import { __, T } from 'stylewhere/shared/i18n'
import { EncodingOperationConfig } from 'stylewhere/shared/RemoteOperation'
import {
  compareSizeLabels,
  enabledEanAndAddon,
  getEncodingProductFields,
  getInitialType,
  getInitialTypeOptions,
  isCanEncodeWithOverQuantity,
  isCheckEncodedQuantity,
  isModalError,
  isOrderCompleted,
  parseProductionOrderRowEanAddonInput,
  showCountersOnEncoding,
  showToastError
} from 'stylewhere/utils'

export const DEFAULT_PRODUCTION_ORDER_ATTRIBUTES = 4

interface Props {
  operation: EncodingOperationConfig
  onSubmit: (data: FormSchemaData) => void | Promise<void>
  onProductionOrderReceived?: (productionOrder: ProductionOrder) => Promise<void>
  onWorkOrderReceived?: (workOrder: WorkOrder, workOrderRows?: WorkOrderRow[]) => Promise<void>
  onWorkOrderDatadogEvent?: (action: string, context?: Record<string, unknown>) => void
  getRunId?: () => string | undefined
  updateFormSchemaData?: (data: FormSchemaData) => void
  onProductionOrderClear?: () => void
  onWorkOrderClear?: () => void
  onProductClear?: () => void
  onIdentifierClear?: () => void
  data?: FormSchemaData
  product?: TmrProduct
  counters?: EncodingCounters
  resetStateCounter?: number
  disabledClear?: boolean
  disabledClearProduct?: boolean
  disableInput?: boolean
  disableWorkOrderRowSelection?: boolean
  overrideFormSchema?: FormSchema
  overrideProductInfoFields?: ProductInfoField[]
  style?: React.CSSProperties
  flex?: boolean
  productionOrderDetail?: () => void
  enableKoSounds?: boolean
}
export interface EncodingFormState {
  data?: FormSchemaData
  productionOrder?: ProductionOrder
  workOrder?: WorkOrder
  workOrderRows?: WorkOrderRow[]
  selectedWorkOrderRowId?: string
  productionOrderRow?: ProductionOrderRow
  resetStateCounter?: number
  loading: boolean
  loadingCounterProductionOrder: boolean
  loadingProductionOrderRow: boolean
  viewProductionOrderAttribute: boolean
  schema: FormSchema
}

export class EncodingForm<T extends EncodingFormState> extends Component<Props, T> {
  productionOrderFields = EncodingExtensions.getProductionOrderFields(this.props.operation)
  isModal = false
  initialType = getInitialType(this.props.operation)

  state: T = {
    data: this.props.data,
    resetStateCounter: this.props.resetStateCounter || 1,
    loading: true,
    loadingCounterProductionOrder: false,
    loadingProductionOrderRow: false,
    viewProductionOrderAttribute: false,
    schema: []
  } as T

  componentDidMount() {
    const { overrideFormSchema, operation } = this.props
    this.isModal = isModalError(operation)
    const { data } = this.props
    if (data?.productionOrder?.id) {
      this.setState((prevState) => {
        return {
          productionOrder: data.productionOrder,
          schema: overrideFormSchema ?? EncodingExtensions.formSchema(operation, prevState.data),
          loading: false
        }
      })
    } else {
      this.setState((prevState) => {
        return {
          schema: overrideFormSchema ?? EncodingExtensions.formSchema(operation, prevState.data),
          loading: false
        }
      })
    }
  }

  shouldComponentUpdate = (nextProps: Props) => {
    const { data, resetStateCounter, loading, loadingCounterProductionOrder, loadingProductionOrderRow, schema } =
      this.state
    const isOrderFormSchema = this.initialType === 'formSchema' && !!schema.some((el) => el.initialType === 'order')
    const isWorkOrder = this.initialType === 'workOrder'
    if (
      !loading &&
      !loadingCounterProductionOrder &&
      !loadingProductionOrderRow &&
      nextProps.data &&
      JSON.stringify(nextProps.data) !== JSON.stringify(data)
    ) {
      if (nextProps.resetStateCounter && resetStateCounter !== nextProps.resetStateCounter) {
        if (this.initialType === 'order' || isOrderFormSchema) {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            productionOrder: nextProps.data.productionOrder,
            productionOrderRow: undefined
          })
        } else if (isWorkOrder) {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            workOrder: undefined,
            workOrderRows: undefined,
            selectedWorkOrderRowId: undefined
          })
        } else {
          this.setState({
            data: nextProps.data,
            resetStateCounter: nextProps.resetStateCounter,
            productionOrderRow: undefined
          })
        }
      } else {
        const discard =
          (this.initialType === 'order' || isOrderFormSchema) &&
          !!nextProps.data.productionOrderRow &&
          !nextProps.data.productionOrder
        if (!discard) this.setState({ data: nextProps.data })
      }
    } else if (
      !loading &&
      !loadingCounterProductionOrder &&
      !loadingProductionOrderRow &&
      nextProps.resetStateCounter &&
      resetStateCounter !== nextProps.resetStateCounter
    ) {
      if (this.initialType === 'order' || isOrderFormSchema) {
        this.setState({
          resetStateCounter: nextProps.resetStateCounter,
          productionOrder: undefined,
          productionOrderRow: undefined
        })
      } else if (isWorkOrder) {
        this.setState({
          resetStateCounter: nextProps.resetStateCounter,
          data: nextProps.data ?? {},
          workOrder: undefined,
          workOrderRows: undefined,
          selectedWorkOrderRowId: undefined
        })
      } else {
        this.setState({ resetStateCounter: nextProps.resetStateCounter, productionOrderRow: undefined })
      }
    } else if (loadingProductionOrderRow && nextProps.data?.productionOrderRow) {
      this.setState({ loadingProductionOrderRow: false })
    }

    return true
  }

  initialTypeProduct = () => {
    const { data, schema } = this.state
    const { disableInput, onSubmit, style, flex, operation } = this.props
    return (
      <FormSchemaForm
        flex={flex === false ? flex : true}
        initialValues={data}
        style={style ?? { justifyContent: 'space-between' }}
        schema={schema}
        onSubmit={onSubmit}
        submitText={__(T.misc.next)}
        disabled={this.initialType === 'formSchema' ? undefined : disableInput}
        operation={operation}
      />
    )
  }

  clearProductionOrderInfo = () => {
    this.props.onProductionOrderClear?.()
    if (this.state.data?.productionOrder)
      this.setState((prevState) => {
        return {
          productionOrderRow: undefined,
          data: { productionOrder: prevState.data.productionOrder }
        }
      })
    else {
      this.setState((prevState) => {
        return { productionOrderRow: undefined }
      })
    }
  }

  clearWorkOrderInfo = () => {
    this.props.onWorkOrderClear?.()
  }

  clearProductInfo = () => {
    this.props.onProductClear?.()
    if (this.state.data?.productionOrder)
      this.setState((prevState) => {
        return {
          productionOrderRow: undefined,
          data: { productionOrder: prevState.data.productionOrder }
        }
      })
    else {
      this.setState((prevState) => {
        return { productionOrderRow: undefined }
      })
    }
  }

  productInfoBox = () => {
    const {
      product,
      counters,
      onIdentifierClear,
      operation,
      disabledClear,
      disabledClearProduct,
      overrideProductInfoFields,
      flex,
      productionOrderDetail
    } = this.props
    const { productionOrderRow, data } = this.state
    return (
      <EncodingProduct
        initialType={this.initialType}
        code={data?.wam}
        operationId={operation.id}
        product={
          {
            ...(product ?? productionOrderRow?.product),
            productionOrderRow: productionOrderRow?.code
          } as any
        }
        clear={onIdentifierClear}
        productClose={disabledClearProduct ? undefined : this.clearProductInfo}
        fields={overrideProductInfoFields ?? getEncodingProductFields()}
        disabledClear={disabledClear}
        flex={flex}
        counters={counters}
        productionOrderDetail={productionOrderDetail}
      />
    )
  }

  formSchemaProductionOrder = () => {
    const { operation, disableInput, flex, style } = this.props
    const { schema } = this.state
    let tmp = schema
    if (this.initialType === 'formSchema') {
      tmp = tmp.filter((el) => el.initialType === 'order' || el.referenceInitialType === 'order')
    }
    return (
      <FormSchemaForm
        key="formProductionOrder"
        flex={flex === false ? flex : true}
        style={style ?? { justifyContent: 'space-between' }}
        schema={tmp}
        onSubmit={this.submitProductionOrder}
        submitText={__(T.misc.next)}
        disabled={disableInput}
        operation={operation}
      />
    )
  }

  resetSelectedWorkOrderRowId = () => {
    this.setState({ selectedWorkOrderRowId: undefined })
  }

  handleWorkOrderRowSelected = async (workOrderRow: WorkOrderRow) => {
    const { workOrder } = this.state
    this.setState({ selectedWorkOrderRowId: workOrderRow.id, loadingProductionOrderRow: true })
    try {
      await this.props.onSubmit({
        workOrder: workOrder.code,
        workOrderId: workOrder.id,
        workOrderRow: workOrderRow.code,
        wordOrderRowId: workOrderRow.id
      })
    } finally {
      this.setState({ loadingProductionOrderRow: false })
    }
  }

  getWorkOrderRowLabel = (workOrderRow: WorkOrderRow) => {
    return workOrderRow.product?.size?.description || workOrderRow.product?.size?.value || workOrderRow.code
  }

  changeWorkOrder = (value: any, formData: any, setFormData: (newFormData: any) => void) => {
    if (value === '') {
      this.clearWorkOrderInfo()
    }
  }

  getEncodedWorkOrderRow = (counter?: EncodingCounterValues) => {
    if (counter) {
      return counter.encoded + counter.pending
    }
    return 0
  }

  getExpectedWorkOrderRow = (counter: EncodingCounterValues) => {
    if (counter) {
      return counter.expected + counter.extra
    }
    return 0
  }

  formSchemaWorkOrder = () => {
    const { operation, disableInput, disableWorkOrderRowSelection, flex, style, counters } = this.props
    const {
      schema,
      workOrderRows,
      data,
      selectedWorkOrderRowId,
      resetStateCounter,
      loadingCounterProductionOrder,
      loadingProductionOrderRow
    } = this.state
    const hasWorkOrderRows = !!workOrderRows && workOrderRows.length > 0
    let tmp = schema
    if (this.initialType === 'formSchema') {
      tmp = tmp.filter((el) => el.initialType === 'workOrder' || el.referenceInitialType === 'workOrder')
    }
    const find = tmp.find((el) => el.name === 'workOrder')
    if (find) {
      find.onChange = this.changeWorkOrder
    }

    return (
      <FormSchemaForm
        key={`formWorkOrder-${resetStateCounter ?? 1}`}
        flex={flex === false ? flex : true}
        style={style ?? { justifyContent: 'space-between' }}
        initialValues={data ?? {}}
        schema={tmp}
        onSubmit={this.submitWorkOrder}
        onCancel={hasWorkOrderRows ? this.clearWorkOrderInfo : undefined}
        cancelText={hasWorkOrderRows ? __(T.misc.clear) : undefined}
        cancelDisabled={loadingProductionOrderRow}
        submitText={hasWorkOrderRows ? undefined : __(T.misc.next)}
        disabled={disableInput}
        operation={operation}
      >
        {hasWorkOrderRows && (
          <>
            {loadingCounterProductionOrder && this.renderLoading()}
            {counters && showCountersOnEncoding(this.initialType, counters) && !loadingCounterProductionOrder && (
              <EncodingCountersBox counters={counters} isWorkOrder />
            )}
            <Spacer height={'10'} />
          </>
        )}
        {operation.options?.workOrderRowSelectionMode === 'sizeGrid' && hasWorkOrderRows && (
          <Box>
            <Text style={{ marginBottom: 10 }} fontSize={16}>
              {__(T.misc.sizes)}
            </Text>
            <SizeGridScrollArea>
              <Grid templateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={4}>
                {workOrderRows
                  .toSorted((a, b) => compareSizeLabels(this.getWorkOrderRowLabel(a), this.getWorkOrderRowLabel(b)))
                  .map((workOrderRow) => {
                    const counter = counters?.rows?.find((row) => row.rowId === workOrderRow.id)
                    const encoded = this.getEncodedWorkOrderRow(counter)
                    const expected = this.getExpectedWorkOrderRow(counter)
                    const isCompleted = expected > 0 && encoded >= expected
                    return (
                      <Button
                        key={workOrderRow.id ?? workOrderRow.code}
                        variant={
                          isCompleted ? 'default' : selectedWorkOrderRowId === workOrderRow.id ? 'primary' : 'secondary'
                        }
                        onClick={
                          selectedWorkOrderRowId || disableWorkOrderRowSelection
                            ? undefined
                            : () => this.handleWorkOrderRowSelected(workOrderRow)
                        }
                        disabled={isCompleted || !!selectedWorkOrderRowId || !!disableWorkOrderRowSelection}
                        disabledOpacity={
                          !!selectedWorkOrderRowId && selectedWorkOrderRowId === workOrderRow.id ? '1' : '0.5'
                        }
                      >
                        <Box flex>
                          <Text bold>{this.getWorkOrderRowLabel(workOrderRow)}</Text>
                          {counter && (
                            <Text style={{ fontWeight: 'normal', fontSize: 14, marginTop: 2 }}>
                              {expected > 0 ? `${encoded} / ${expected}` : `${encoded}`}
                            </Text>
                          )}
                        </Box>
                      </Button>
                    )
                  })}
              </Grid>
            </SizeGridScrollArea>
          </Box>
        )}
      </FormSchemaForm>
    )
  }

  renderLoading = () => {
    return (
      <KeyValueRow style={{ justifyContent: 'flex-start', alignItems: 'center' }}>
        <Spinner color="#454545" />
        <Text style={{ marginLeft: 10 }} center fontSize={14}>
          {__(T.misc.loading)}
        </Text>
      </KeyValueRow>
    )
  }

  managerViewProductionOrderAttribute = () => {
    this.setState((prevState) => ({ viewProductionOrderAttribute: !prevState.viewProductionOrderAttribute }))
  }

  getProductionOrderFields = () => {
    const { viewProductionOrderAttribute } = this.state
    if (viewProductionOrderAttribute) {
      return this.productionOrderFields
    }
    return this.productionOrderFields.slice(0, DEFAULT_PRODUCTION_ORDER_ATTRIBUTES)
  }

  formSchemaProductionOrderRow() {
    const { disableInput, operation, counters, productionOrderDetail, flex, style } = this.props
    const { productionOrder, productionOrderRow, loadingCounterProductionOrder, loadingProductionOrderRow, schema } =
      this.state
    const displayProductionOrder = productionOrder ?? productionOrderRow?.productionOrder
    let tmp = schema
    if (this.initialType === 'formSchema') {
      tmp = tmp.filter((el) => el.initialType === 'orderRow' || el.referenceInitialType === 'orderRow')
    }
    return (
      <Box flex={flex === false ? 0 : 1}>
        {displayProductionOrder && (
          <>
            <InfoBox
              title={__(T.misc.production_order)}
              subtitle={displayProductionOrder?.code}
              data={displayProductionOrder}
              fields={this.getProductionOrderFields()}
              showMore={
                this.productionOrderFields.length > DEFAULT_PRODUCTION_ORDER_ATTRIBUTES
                  ? () => this.managerViewProductionOrderAttribute()
                  : undefined
              }
              viewMore={this.state.viewProductionOrderAttribute}
              onCloseClick={this.clearProductionOrderInfo}
            />
            <Spacer height={'10'} />
            {!productionOrderRow && loadingCounterProductionOrder && (
              <>
                {this.renderLoading()}
                <Spacer height={'10'} />
              </>
            )}
            {(!productionOrderRow || loadingProductionOrderRow) &&
              counters &&
              showCountersOnEncoding(this.initialType, counters) &&
              !loadingCounterProductionOrder && (
                <>
                  <EncodingCountersBox counters={counters} productionOrderDetail={productionOrderDetail} />
                  <Spacer height={'10'} />
                </>
              )}
          </>
        )}
        {!!productionOrderRow && !loadingProductionOrderRow && this.productInfoBox()}
        {loadingProductionOrderRow && this.renderLoading()}
        {!productionOrderRow &&
          !loadingProductionOrderRow &&
          !isOrderCompleted(counters, isCheckEncodedQuantity(operation), isCanEncodeWithOverQuantity(operation)) && (
            <FormSchemaForm
              key="formProductionOrderRow"
              flex={flex === false ? flex : true}
              style={style ?? { justifyContent: 'space-between' }}
              schema={tmp}
              onSubmit={this.submitProductionOrderRow}
              submitText={__(T.misc.next)}
              disabled={disableInput}
              operation={operation}
            />
          )}
      </Box>
    )
  }

  submitProductionOrder = async (data: { productionOrder: string }) => {
    const { onProductionOrderReceived, updateFormSchemaData, operation } = this.props
    try {
      if (data.productionOrder) {
        const initialTypeOptions = getInitialTypeOptions(this.props.operation)
        const additionaParameters = initialTypeOptions?.productionOrdersParams || {}
        const res = await ProductionOrders.search<ProductionOrder>({
          equalCodes: data.productionOrder,
          ...additionaParameters
        })
        if (res.content.length === 0) {
          throw new Error('Production order not found')
        }
        const productionOrder = res.content[0]
        if (this.checkWorkerOnProductionOrder() === 'yes') {
          const wsPlace = AppStore.getWorkstationPlace()
          if (
            wsPlace &&
            productionOrder.worker.code !== wsPlace.code &&
            !AppStore.hasAnyCapabilities('System.User.allPlaceAccess')
          ) {
            showToastError(__(T.error.WRONG_PLACE), __(T.error.error), this.isModal)
            return
          }
        } else if (this.checkWorkerOnProductionOrder() === 'userPlaces') {
          const user = AppStore.loggedUser
          const userPlaces = user?.userPlaces
          if (userPlaces && userPlaces.length > 0) {
            const workerCode = productionOrder.worker.code
            const userPlaceCodes = userPlaces.map((place) => place.code)
            const hasAccess = userPlaceCodes.includes(workerCode)

            if (!hasAccess && !AppStore.hasAnyCapabilities('System.User.allPlaceAccess')) {
              showToastError(__(T.error.WRONG_PLACE), __(T.error.error), this.isModal)
              return
            }
          }
        }
        this.setState({ data, productionOrder, schema: EncodingExtensions.formSchema(operation, data) })
        if (onProductionOrderReceived) {
          this.setState({ loadingCounterProductionOrder: true })
          await onProductionOrderReceived(productionOrder)
          this.setState({ loadingCounterProductionOrder: false })
        }
        if (updateFormSchemaData) {
          updateFormSchemaData({ ...data, productionOrder })
        }
      }
    } catch (error) {
      this.onSubmitError(error)
      showToastError(
        __(T.error.couldn_find_production_order),
        __(T.error.error),
        this.isModal,
        undefined,
        undefined,
        this.props.enableKoSounds
      )
    }
  }

  submitWorkOrder = async (data: { workOrder: string }) => {
    const { onWorkOrderReceived, onWorkOrderDatadogEvent, updateFormSchemaData, operation } = this.props
    try {
      if (data.workOrder) {
        if (operation.options?.workOrderRowSelectionMode === 'sizeGrid') {
          const workOrderRes = await WorkOrders.search<WorkOrder>({
            equalCodes: data.workOrder,
            size: 1
          })
          if (workOrderRes.content.length === 0) {
            onWorkOrderDatadogEvent?.('workOrderNotFound', { workOrder: data.workOrder })
            throw new Error(__(T.error.couldn_find_work_order))
          }
          const workOrder = workOrderRes.content[0]
          const res = await WorkOrderRows.search<WorkOrderRow>({
            equalWorkOrderCodes: data.workOrder,
            size: 100
          })
          if (res.content.length === 0) {
            onWorkOrderDatadogEvent?.('workOrderNotFound', { workOrder: data.workOrder })
            throw new Error(__(T.error.couldn_find_work_order))
          }
          this.setState(
            {
              data,
              workOrder,
              workOrderRows: res.content,
              schema: EncodingExtensions.formSchema(operation, data)
            },
            async () => {
              if (onWorkOrderReceived) {
                this.setState({ loadingCounterProductionOrder: true })
                await onWorkOrderReceived(workOrder, res.content)
                this.setState({ loadingCounterProductionOrder: false })
              }
              if (res.content.length === 1) {
                await this.handleWorkOrderRowSelected(res.content[0])
              }
              if (updateFormSchemaData) {
                updateFormSchemaData({ ...data, workOrder: data.workOrder })
              }
            }
          )
        }
      } else {
        throw new Error('Work order row selection mode not supported')
      }
    } catch (error) {
      this.setState({ loadingCounterProductionOrder: false })
      this.onSubmitError(error)
      showToastError(error, __(T.error.error), this.isModal, undefined, undefined, this.props.enableKoSounds)
    }
  }

  submitProductionOrderRow = async (data: { productionOrder: string; productionOrderRow: string }) => {
    if (!data || (!data.productionOrderRow && !data.productionOrder)) {
      return
    }
    this.setState((prevState) => {
      return {
        loadingProductionOrderRow: true,
        data: {
          ...data,
          ...prevState.data,
          productionOrderRow: data.productionOrderRow
        }
      }
    }, this.processProductionOrderRow)
  }

  processProductionOrderRow = async () => {
    const { productionOrder, data, schema } = this.state
    const { onSubmit, getRunId } = this.props
    try {
      let productionOrderRow
      const mode = this.getOrderSelectionMode()
      const isEanAndAddonEnabled = enabledEanAndAddon(this.props.operation)
      const isOrderFormSchema = this.initialType === 'formSchema' && !!schema.some((el) => el.initialType === 'order')
      const productionOrderCodes =
        this.initialType === 'order' || isOrderFormSchema ? [data.productionOrder.code ?? data.productionOrder] : []
      let param
      if (isEanAndAddonEnabled) {
        const parsedInput = parseProductionOrderRowEanAddonInput(data.productionOrderRow)
        if (!parsedInput) {
          throw new Error('Production order row not found')
        }
        param = {
          equalProductionOrderCodes: productionOrderCodes,
          equalProductCodes: parsedInput.productCode,
          attributes: parsedInput.attributes
        }
      } else if (mode === 'byOrderAndSku') {
        param = {
          equalProductionOrderCodes: productionOrderCodes,
          equalProductCodes: data.productionOrderRow
        }
      } else {
        param = {
          equalProductionOrderCodes: productionOrderCodes,
          equalCodes: data.productionOrderRow
        }
      }
      const runId = getRunId?.()
      const res = await EncodingProductionOrderRows.searchWithRunId<ProductionOrderRow>(
        param,
        runId,
        this.props.operation.id
      )
      if (res?.content?.length > 0) {
        productionOrderRow = res.content[0]
        const find = schema.findIndex((el) => el.initialType === 'orderRow')
        if (find >= 0) schema[find].submitted = true
        this.setState({ productionOrderRow, schema })

        if (this.initialType === 'order' || isOrderFormSchema)
          onSubmit({ ...data, productionOrder, productionOrderRow })
        else onSubmit({ ...data, productionOrderRow })
      } else {
        this.setState({ loadingProductionOrderRow: false })
        throw new Error('Production order row not found')
      }
    } catch (error) {
      this.setState({ loadingProductionOrderRow: false })
      this.onSubmitError(error)
      showToastError(
        __(T.error.couldn_find_production_order_row),
        __(T.error.error),
        this.isModal,
        undefined,
        undefined,
        this.props.enableKoSounds
      )
    }
  }

  onSubmitError = (_error: any) => {
    // Overridden in subclasses
  }

  getOrderSelectionMode = () => {
    const { operation } = this.props
    return operation.options?.productionOrderRowSelectionMode
      ? operation.options.productionOrderRowSelectionMode
      : 'byOrderAndRowCode'
  }

  checkWorkerOnProductionOrder = () => {
    const { operation } = this.props
    return operation?.options?.checkWorkerOnProductionOrder
  }

  render() {
    const { data, loading, schema } = this.state
    const { product } = this.props
    const isProductFormSchema = schema.some((el) => el.isProductCode || el.initialType === 'product')
    const isOrderFormSchema = schema.some((el) => el.initialType === 'order')
    if (loading) {
      return (
        <LoadingContainer>
          <Spinner color="#454545" />
          <Text style={{ marginTop: 15 }} center fontSize={14}>
            {__(T.misc.loading)}
          </Text>
        </LoadingContainer>
      )
    }

    if (['product', 'wam'].includes(this.initialType) || (this.initialType === 'formSchema' && isProductFormSchema)) {
      if (product) return this.productInfoBox()
      return this.initialTypeProduct()
    }

    // Initial type is production order
    if (this.initialType === 'order' || (this.initialType === 'formSchema' && isOrderFormSchema)) {
      if (!data?.productionOrder) return this.formSchemaProductionOrder()
      return this.formSchemaProductionOrderRow()
    }

    if (this.initialType === 'workOrder') {
      return this.formSchemaWorkOrder()
    }

    // Initial type is order row
    if (
      this.initialType === 'orderRow' ||
      (this.initialType === 'formSchema' && !isProductFormSchema && !isOrderFormSchema)
    )
      return this.formSchemaProductionOrderRow()

    return null
  }
}

const LoadingContainer = styled(Box)`
  background: #ffffff;
  box-sizing: border-box;
  border-radius: 15px;
  padding: 20px;
  justify-content: center;
  align-items: center;
  flex: 1;
`

/** Scroll only the size buttons; keeps Clear button visible without changing form layout. */
const SizeGridScrollArea = styled.div`
  max-height: min(45vh, 420px);
  overflow-y: auto;
`
